import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PanelShell } from '../panel-shell.js';
import { Badge } from '../badge.js';
import { EntityRowContextMenu } from '../entity-row-context-menu.js';
import { useActiveCountry, useSetPanelEntity, type PanelId, type TabId } from '../store.js';
import { authHeaders } from '../../api/client.js';
import { API_BASE_URL } from '../../api/base-url.js';

// Landing-tab panel 1 of 5: Compound-signal threshold alerts.
// Single-instance, narrow column (~18% of canvas). Lists entities
// whose primary archetype score crossed above a configured threshold.
//
// Honours the workspace-level activeCountry filter — the leaderboard
// endpoint accepts `?country=` so we narrow server-side.

const ARCHETYPES = ['classic_succession', 'quiet_distress', 'cleanup_pattern'] as const;

interface LeaderboardRow {
  id: string;
  countryCode: string;
  legalName: string | null;
  subSegment: string | null;
  archetype: string;
  score: string;
  coverage: string;
}

async function fetchTop(country: string | null): Promise<LeaderboardRow[]> {
  const countryParam = country ? `&country=${country}` : '';
  const fetches = ARCHETYPES.map(async (a) => {
    const r = await fetch(
      `${API_BASE_URL}/signals/leaderboard?archetype=${a}&limit=10${countryParam}`,
      {
        headers: await authHeaders(),
      },
    );
    if (!r.ok) return [] as LeaderboardRow[];
    const j = (await r.json()) as { data: LeaderboardRow[] };
    return j.data;
  });
  const all = (await Promise.all(fetches)).flat();
  return all.sort((a, b) => Number(b.score) - Number(a.score)).slice(0, 20);
}

export function CompoundSignalAlertsPanel({ tabId, panelId }: { tabId: TabId; panelId: PanelId }) {
  const { t } = useTranslation();
  const select = useSetPanelEntity(tabId, panelId);
  const activeCountry = useActiveCountry();
  const { data, isLoading, error } = useQuery({
    queryKey: ['landing', 'compound-alerts', activeCountry],
    queryFn: () => fetchTop(activeCountry),
    staleTime: 5 * 60_000,
  });

  const rows = data ?? [];
  const showEmpty = !isLoading && !error && rows.length === 0;

  return (
    <PanelShell
      tabId={tabId}
      panelId={panelId}
      toolbar={<SectionHeader text={t('panels.landing.alerts.label')} />}
      loading={isLoading}
      error={error ? t('panels.lists.error') : false}
      empty={showEmpty ? t('panels.landing.alerts.empty') : false}
      padding="var(--s-3)"
    >
      <table className="table">
        <thead>
          <tr>
            <th>{t('panels.lists.columns.legalName')}</th>
            <th>{t('panels.landing.alerts.archetype')}</th>
            <th className="num">{t('panels.landing.alerts.score')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <EntityRowContextMenu
              key={`${r.id}-${r.archetype}`}
              entityId={r.id}
              legalName={r.legalName}
            >
              <tr onClick={() => select(r.id)} style={{ cursor: 'pointer' }}>
                <td>
                  <strong>{r.legalName ?? '—'}</strong>{' '}
                  <span style={{ color: 'var(--text-tertiary)' }}>· {r.countryCode}</span>
                </td>
                <td>
                  <Badge variant={r.archetype === 'quiet_distress' ? 'alert' : 'accent'}>
                    {t(`archetypes.${r.archetype}`)}
                  </Badge>
                </td>
                <td className="num" data-numeric>
                  {Number(r.score).toFixed(0)}
                </td>
              </tr>
            </EntityRowContextMenu>
          ))}
        </tbody>
      </table>
    </PanelShell>
  );
}

function SectionHeader({ text }: { text: string }) {
  return (
    <div className="section-header" style={{ padding: 'var(--s-2) var(--s-3)', marginBottom: 0 }}>
      {text}
    </div>
  );
}
